// Admin endpoints for the Research Dialogue Library (core-lambda).
// core-lambda reads the PLATFORM db directly and proxies every write to aris-ai,
// which is the library's only writer. All routes require an admin token.

import axios from "axios";
import { axiosAssessmentInstance } from "../AxiosConfiguration";

const unwrap = (response, fallback = null) =>
  response?.data?.payload ?? fallback;

const BASE = "/api/admin/research-library";

const id = (value) => encodeURIComponent(String(value ?? ""));

// ─── Library ───────────────────────────────────────────────────────────────

export const getResearchLibrarySummary = async () => {
  const response = await axiosAssessmentInstance.get(`${BASE}/summary`);
  return unwrap(response, {});
};

/**
 * @param {{page?: number, page_size?: number, search?: string, life_area?: string,
 *   dialogue_type?: "FOUNDATIONAL"|"COMPOSITE", status?: "attachable"|"held",
 *   reason?: string}} params
 */
export const listResearchDialogues = async (params = {}) => {
  const response = await axiosAssessmentInstance.get(`${BASE}/dialogues`, {
    params,
  });
  const payload = unwrap(response, {});
  const items = payload?.items || [];
  const pageSize = payload?.page_size || params.page_size || 20;
  const total = payload?.total ?? items.length;
  return {
    items,
    total,
    page: payload?.page ?? params.page ?? 1,
    page_size: pageSize,
    total_pages: Math.max(1, Math.ceil(total / pageSize)),
  };
};

export const getResearchDialogue = async (dialogueId) => {
  const response = await axiosAssessmentInstance.get(
    `${BASE}/dialogues/${id(dialogueId)}`
  );
  return unwrap(response);
};

/** `patch` keys: member_title, description — omitted = unchanged, null = clear. */
export const updateResearchDialogue = async (dialogueId, patch) => {
  const response = await axiosAssessmentInstance.patch(
    `${BASE}/dialogues/${id(dialogueId)}`,
    patch
  );
  return unwrap(response);
};

export const holdResearchDialogue = async (dialogueId, reason) => {
  const response = await axiosAssessmentInstance.post(
    `${BASE}/dialogues/${id(dialogueId)}/hold`,
    { reason }
  );
  return unwrap(response);
};

export const releaseResearchDialogue = async (dialogueId) => {
  const response = await axiosAssessmentInstance.post(
    `${BASE}/dialogues/${id(dialogueId)}/release`
  );
  return unwrap(response);
};

// ─── Artwork ───────────────────────────────────────────────────────────────
// A render becomes a candidate (pending_review); only approve makes it live.

// core-lambda's payload is the Artwork itself; tolerate aris-ai's {artwork} shape.
const unwrapArtwork = (response) => {
  const payload = unwrap(response);
  return payload?.artwork ?? payload;
};

export const getDialogueArtwork = async (dialogueId) => {
  const response = await axiosAssessmentInstance.get(
    `${BASE}/dialogues/${id(dialogueId)}/artwork`
  );
  return unwrapArtwork(response);
};

/**
 * Starts a background render (202). Omitted keys are not sent: no prompt = the
 * saved override, else the library prompt; reset_prompt clears the override first.
 *
 * @param {string} dialogueId
 * @param {string} kind "banner", "card" or a "variant_*" image (plan 44)
 * @param {{prompt?: string, save_prompt?: boolean, reset_prompt?: boolean}} [options]
 */
export const regenerateDialogueArtwork = async (
  dialogueId,
  kind,
  { prompt, save_prompt, reset_prompt } = {}
) => {
  const body = {};
  if (prompt != null) body.prompt = prompt;
  if (save_prompt != null) body.save_prompt = save_prompt;
  if (reset_prompt != null) body.reset_prompt = reset_prompt;
  const response = await axiosAssessmentInstance.post(
    `${BASE}/dialogues/${id(dialogueId)}/artwork/${id(kind)}/regenerate`,
    body
  );
  return unwrapArtwork(response);
};

/** `version` is the candidate version the admin looked at (409 if it changed). */
export const approveDialogueArtwork = async (dialogueId, kind, version) => {
  const response = await axiosAssessmentInstance.post(
    `${BASE}/dialogues/${id(dialogueId)}/artwork/${id(kind)}/approve`,
    { version }
  );
  return unwrapArtwork(response);
};

// ─── Imports ───────────────────────────────────────────────────────────────

/** @returns {Promise<{import: object, uploads: Array<{file, url, method, headers}>}>} */
export const createResearchImport = async ({ files, release_version } = {}) => {
  const body = { files };
  if (release_version) body.release_version = release_version;
  const response = await axiosAssessmentInstance.post(`${BASE}/imports`, body);
  return unwrap(response, {});
};

export const validateResearchImport = async (importId) => {
  const response = await axiosAssessmentInstance.post(
    `${BASE}/imports/${id(importId)}/validate`
  );
  return unwrap(response, {});
};

export const commitResearchImport = async (importId, { embed = true } = {}) => {
  const response = await axiosAssessmentInstance.post(
    `${BASE}/imports/${id(importId)}/commit`,
    { embed }
  );
  return unwrap(response, {});
};

export const getResearchImport = async (importId) => {
  const response = await axiosAssessmentInstance.get(
    `${BASE}/imports/${id(importId)}`
  );
  return unwrap(response, {});
};

export const listResearchImports = async ({ limit = 20 } = {}) => {
  const response = await axiosAssessmentInstance.get(`${BASE}/imports`, {
    params: { limit },
  });
  return unwrap(response, { imports: [] });
};

/**
 * PUT one release file to its presigned S3 URL. Bare axios (no interceptors):
 * the URL carries S3 auth, and our Bearer token must never be sent to S3.
 * Sends exactly the headers the API returned.
 *
 * @param {{url: string, method?: string, headers?: object}} upload
 * @param {File} file
 * @param {(fraction: number) => void} [onProgress] 0..1
 */
export const uploadReleaseFile = async (upload, file, onProgress) =>
  axios.put(upload.url, file, {
    headers: { ...(upload.headers || {}) },
    onUploadProgress: (event) => {
      if (!onProgress) return;
      const total = event?.total || file?.size || 0;
      onProgress(total ? Math.min(1, (event?.loaded || 0) / total) : 0);
    },
  });
